import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Mouse, MousePointer, Zap } from 'lucide-react';

export const MouseSettings: React.FC = () => {
  const [sensitivity, setSensitivity] = useState([50]);
  const [doubleClickSpeed, setDoubleClickSpeed] = useState([50]);
  const [scrollSpeed, setScrollSpeed] = useState([3]);
  const [pointerSpeed, setPointerSpeed] = useState([6]);
  const [leftHanded, setLeftHanded] = useState(false);
  const [enhancePrecision, setEnhancePrecision] = useState(true);
  const [showTrails, setShowTrails] = useState(false);
  const [trailLength, setTrailLength] = useState([5]);
  const [pointerScheme, setPointerScheme] = useState('default');
  const [scrollDirection, setScrollDirection] = useState('natural');

  return (
    <div className="h-full p-6 bg-background overflow-auto">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground mb-2">Mouse Settings</h1>
          <p className="text-muted-foreground">
            Configure mouse behavior and pointer options
          </p>
        </div>

        {/* Basic Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mouse size={20} />
              Basic Settings
            </CardTitle>
            <CardDescription>
              Primary mouse configuration
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <Label htmlFor="left-handed">Left-handed mode</Label>
              <Switch
                id="left-handed"
                checked={leftHanded}
                onCheckedChange={setLeftHanded}
              />
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="sensitivity">Mouse Sensitivity</Label>
                <span className="text-sm text-muted-foreground">
                  {sensitivity[0]}%
                </span>
              </div>
              <Slider
                id="sensitivity"
                value={sensitivity}
                onValueChange={setSensitivity}
                max={100}
                step={1}
                className="w-full"
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="double-click-speed">Double-click Speed</Label>
                <span className="text-sm text-muted-foreground">
                  {doubleClickSpeed[0] < 30 ? 'Slow' : doubleClickSpeed[0] > 70 ? 'Fast' : 'Medium'}
                </span>
              </div>
              <Slider
                id="double-click-speed"
                value={doubleClickSpeed}
                onValueChange={setDoubleClickSpeed}
                max={100}
                step={1}
                className="w-full"
              />
              <div className="flex justify-center">
                <div className="w-16 h-16 border-2 border-dashed border-muted-foreground rounded-lg flex items-center justify-center text-xs text-muted-foreground">
                  Test Area
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pointer Options */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MousePointer size={20} />
              Pointer Options
            </CardTitle>
            <CardDescription>
              Customize pointer appearance and behavior
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="pointer-speed">Pointer Speed</Label>
                <span className="text-sm text-muted-foreground">
                  {pointerSpeed[0]}/10
                </span>
              </div>
              <Slider
                id="pointer-speed"
                value={pointerSpeed}
                onValueChange={setPointerSpeed}
                min={1}
                max={10}
                step={1}
                className="w-full"
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="enhance-precision">Enhance pointer precision</Label>
              <Switch
                id="enhance-precision"
                checked={enhancePrecision}
                onCheckedChange={setEnhancePrecision}
              />
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="pointer-scheme">Pointer Scheme</Label>
              <Select value={pointerScheme} onValueChange={setPointerScheme}>
                <SelectTrigger>
                  <SelectValue placeholder="Select pointer scheme" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default (Black)</SelectItem>
                  <SelectItem value="white">White</SelectItem>
                  <SelectItem value="large">Large</SelectItem>
                  <SelectItem value="extra-large">Extra Large</SelectItem>
                  <SelectItem value="animated">Animated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="show-trails">Show pointer trails</Label>
              <Switch
                id="show-trails"
                checked={showTrails}
                onCheckedChange={setShowTrails}
              />
            </div>

            {showTrails && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label htmlFor="trail-length">Trail Length</Label>
                  <span className="text-sm text-muted-foreground">
                    {trailLength[0] < 3 ? 'Short' : trailLength[0] > 7 ? 'Long' : 'Medium'}
                  </span>
                </div>
                <Slider
                  id="trail-length"
                  value={trailLength}
                  onValueChange={setTrailLength}
                  min={1}
                  max={10}
                  step={1}
                  className="w-full"
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scroll Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Scroll Settings</CardTitle>
            <CardDescription>
              Configure scrolling behavior
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="scroll-speed">Scroll Speed</Label>
                <span className="text-sm text-muted-foreground">
                  {scrollSpeed[0]} lines
                </span>
              </div>
              <Slider
                id="scroll-speed"
                value={scrollSpeed}
                onValueChange={setScrollSpeed}
                min={1}
                max={10}
                step={1}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="scroll-direction">Scroll Direction</Label>
              <Select value={scrollDirection} onValueChange={setScrollDirection}>
                <SelectTrigger>
                  <SelectValue placeholder="Select scroll direction" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="natural">Natural (Reverse)</SelectItem>
                  <SelectItem value="traditional">Traditional</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Advanced Options */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Zap size={20} />
              Advanced Options
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="snap-to-default">Snap to default button</Label>
              <Switch id="snap-to-default" />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="hide-while-typing">Hide pointer while typing</Label>
              <Switch id="hide-while-typing" />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="show-location">Show pointer location when Ctrl is pressed</Label>
              <Switch id="show-location" />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline">Reset to Defaults</Button>
          <Button>Apply Changes</Button>
        </div>
      </div>
    </div>
  );
};